import { useState, useEffect, useMemo, useCallback } from 'react';

import { debugAttachmentScope } from '../dev/attachmentScopeDebug';
import {
  attachmentService,
  SavedAttachment,
} from '../services/attachmentService';

export interface UseAttachmentsResult {
  attachments: SavedAttachment[];
  attachmentsBySection: Record<string, SavedAttachment[]>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Get attachment count for a specific section */
  getCountForSection: (sectionId: string) => number;
  /** Get all unique section IDs that have attachments */
  getSectionIds: () => string[];
  /** Get all attachments excluding a specific section (useful for aggregation) */
  getAttachmentsExcludingSection: (sectionId: string) => SavedAttachment[];
}

export const useAttachments = (
  requestId: string | null,
): UseAttachmentsResult => {
  const [attachments, setAttachments] = useState<SavedAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoized so its identity is stable across renders for any given requestId.
  // Consumers (notably RequestDetail) put `refetch` in useEffect deps; without
  // useCallback, every render produced a new reference, retriggering effects
  // and causing concurrent fetch storms when a transcription job completed
  // (root cause of "Supporting documents empty / screen blinks" — QA-52).
  const fetchAttachments = useCallback(async () => {
    if (!requestId) {
      setAttachments([]);
      return;
    }

    // Skip API call for offline requests - they don't have attachments on server yet
    if (requestId.startsWith('o-')) {
      setAttachments([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await attachmentService.getAttachmentsByRequest(
        parseInt(requestId),
      );
      if (result.success && result.attachments) {
        setAttachments(result.attachments);
        debugAttachmentScope('useAttachments.fetch', {
          requestId,
          attachmentCount: result.attachments.length,
          sectionIds: [
            ...new Set(
              result.attachments.map(a => a.requestSection ?? '(missing)'),
            ),
          ],
          attachmentIds: result.attachments.map(a => a.id),
        });
      } else {
        setError(result.error || 'Failed to load attachments');
        setAttachments([]);
      }
    } catch (err) {
      console.error('useAttachments: fetch error:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load attachments',
      );
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  // Group attachments by section (memoized)
  const attachmentsBySection = useMemo(() => {
    return attachments.reduce((acc, attachment) => {
      const section = attachment.requestSection || 'unknown';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(attachment);
      return acc;
    }, {} as Record<string, SavedAttachment[]>);
  }, [attachments]);

  // Get attachment count for a specific section
  const getCountForSection = useCallback(
    (sectionId: string): number => {
      return attachmentsBySection[sectionId]?.length || 0;
    },
    [attachmentsBySection],
  );

  // Get all unique section IDs that have attachments
  const getSectionIds = useCallback((): string[] => {
    return Object.keys(attachmentsBySection);
  }, [attachmentsBySection]);

  // Get all attachments excluding a specific section (useful for aggregation in Supporting Documents)
  const getAttachmentsExcludingSection = useCallback(
    (sectionId: string): SavedAttachment[] => {
      return attachments.filter(
        attachment => attachment.requestSection !== sectionId,
      );
    },
    [attachments],
  );

  return {
    attachments,
    attachmentsBySection,
    loading,
    error,
    refetch: fetchAttachments,
    getCountForSection,
    getSectionIds,
    getAttachmentsExcludingSection,
  };
};
